/**
 * Invitation Service
 * Handles board invitation operations
 * 
 * @module services/invitationService
 */

import { supabase } from './supabase';
import { getCurrentUserId } from './authService';
import { ServiceResult, ok, err } from '../types/api/result';
import { AppError } from '../lib/errors/AppError';
import { logger } from '../lib/logger';
import { createNotification } from './notificationService';

// =============================================================================
// Types
// =============================================================================

export interface BoardInvitation {
    id: string;
    boardId: string;
    inviterId: string;
    inviteeEmail: string;
    inviteeId: string | null;
    role: 'viewer' | 'commenter' | 'editor';
    status: 'pending' | 'accepted' | 'declined' | 'expired';
    createdAt: string;
    respondedAt: string | null;
    expiresAt: string;
    // Joined data
    boardTitle?: string;
    inviterName?: string;
    inviterAvatar?: string;
}

interface InvitationRow {
    id: string;
    board_id: string;
    inviter_id: string;
    invitee_email: string;
    invitee_id: string | null;
    role: string;
    status: string;
    created_at: string;
    responded_at: string | null;
    expires_at: string;
    boards?: { title: string };
    profiles?: { full_name: string; avatar_url: string };
}

// =============================================================================
// Helpers
// =============================================================================

function toInvitation(row: InvitationRow): BoardInvitation {
    return {
        id: row.id,
        boardId: row.board_id,
        inviterId: row.inviter_id,
        inviteeEmail: row.invitee_email,
        inviteeId: row.invitee_id,
        role: row.role as 'viewer' | 'commenter' | 'editor',
        status: row.status as BoardInvitation['status'],
        createdAt: row.created_at,
        respondedAt: row.responded_at,
        expiresAt: row.expires_at,
        boardTitle: row.boards?.title,
        inviterName: row.profiles?.full_name,
        inviterAvatar: row.profiles?.avatar_url,
    };
}

// =============================================================================
// Service Operations
// =============================================================================

/**
 * Create a new board invitation
 */
export async function createInvitation(
    boardId: string,
    email: string,
    role: 'viewer' | 'commenter' | 'editor' = 'editor'
): Promise<ServiceResult<BoardInvitation>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            return err(new AppError('User not authenticated', { code: 'UNAUTHENTICATED', statusCode: 401 }));
        }

        // Check if invitation already exists
        const { data: existing } = await supabase
            .from('board_invitations')
            .select('id, status')
            .eq('board_id', boardId)
            .eq('invitee_email', email.toLowerCase())
            .eq('status', 'pending')
            .single();

        if (existing) {
            return err(new AppError('Invitation already sent to this email', { code: 'INVITATION_EXISTS' }));
        }

        // Get board title for notification
        const { data: board } = await supabase
            .from('boards')
            .select('title')
            .eq('id', boardId)
            .single();

        // Get inviter profile
        const { data: inviter } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', userId)
            .single();

        // Create invitation
        const { data, error } = await supabase
            .from('board_invitations')
            .insert({
                board_id: boardId,
                inviter_id: userId,
                invitee_email: email.toLowerCase(),
                role,
            })
            .select()
            .single();

        if (error) throw error;

        logger.debug('[InvitationService] Invitation created', { id: data.id });

        // Check if invitee is already a registered user
        const { data: inviteeProfile, error: profileError } = await supabase
            .from('profiles')
            .select('id, email')
            .eq('email', email.toLowerCase())
            .single();

        logger.debug('[InvitationService] Profile lookup result', {
            email: email.toLowerCase(),
            found: !!inviteeProfile,
            error: profileError
        });

        if (inviteeProfile) {
            // Update invitation with invitee_id
            await supabase
                .from('board_invitations')
                .update({ invitee_id: inviteeProfile.id })
                .eq('id', data.id);

            logger.debug('[InvitationService] Creating notification', { inviteeId: inviteeProfile.id });

            // Create in-app notification with detailed information
            const inviterName = inviter?.full_name || 'A team member';
            const boardName = board?.title || 'a collaborative board';
            const roleLabel = role === 'editor' ? 'an editor' : role === 'commenter' ? 'a commenter' : 'a viewer';

            const notifResult = await createNotification(
                inviteeProfile.id,
                'board_invitation',
                `Board Invitation`,
                `${inviterName} invited you to "${boardName}" as ${roleLabel}`,
                {
                    invitation_id: data.id,
                    board_id: boardId,
                    board_title: board?.title,
                    inviter_name: inviterName,
                    role
                }
            );

            logger.debug('[InvitationService] Notification creation result', { success: !!notifResult });
        } else {
            logger.info('[InvitationService] No profile found for email, skipping notification');
        }

        logger.info('[InvitationService] Invitation created', { boardId, email, role });
        return ok(toInvitation(data));

    } catch (error) {
        logger.error('[InvitationService] createInvitation failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Get pending invitations for current user
 */
export async function getPendingInvitations(): Promise<ServiceResult<BoardInvitation[]>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) return ok([]);

        // Get user email from profiles
        const { data: profile } = await supabase
            .from('profiles')
            .select('email')
            .eq('id', userId)
            .single();

        if (!profile?.email) return ok([]);

        const { data, error } = await supabase
            .from('board_invitations')
            .select('*')
            .or(`invitee_id.eq.${userId},invitee_email.eq.${profile.email}`)
            .eq('status', 'pending')
            .gt('expires_at', new Date().toISOString())
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Fetch board titles and inviter names separately
        const invitations = data || [];
        const boardIds = [...new Set(invitations.map(i => i.board_id))];
        const inviterIds = [...new Set(invitations.map(i => i.inviter_id))];

        let boardTitles: Record<string, string> = {};
        if (boardIds.length > 0) {
            const { data: boards } = await supabase
                .from('boards')
                .select('id, title')
                .in('id', boardIds);
            boardTitles = (boards || []).reduce((acc, b) => ({ ...acc, [b.id]: b.title }), {});
        }

        let inviterNames: Record<string, string> = {};
        if (inviterIds.length > 0) {
            const { data: profiles } = await supabase
                .from('profiles')
                .select('id, full_name, email')
                .in('id', inviterIds);

            inviterNames = (profiles || []).reduce((acc, p) => ({
                ...acc,
                [p.id]: p.full_name || p.email || 'Someone'
            }), {});
        }

        return ok(invitations.map(row => ({
            ...toInvitation(row),
            boardTitle: boardTitles[row.board_id] || 'Unknown Board',
            inviterName: inviterNames[row.inviter_id] || 'Someone'
        })));

    } catch (error) {
        // Detailed logging for debugging RLS/Auth issues
        logger.error('[InvitationService] getPendingInvitations failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Get invitations sent for a board (for board owners)
 */
export async function getBoardInvitations(boardId: string): Promise<ServiceResult<BoardInvitation[]>> {
    try {
        const { data, error } = await supabase
            .from('board_invitations')
            .select('*')
            .eq('board_id', boardId)
            .order('created_at', { ascending: false });

        if (error) throw error;

        return ok((data || []).map(toInvitation));

    } catch (error) {
        logger.error('[InvitationService] getBoardInvitations failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Accept an invitation
 */
export async function acceptInvitation(invitationId: string): Promise<ServiceResult<void>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            return err(new AppError('User not authenticated', { code: 'UNAUTHENTICATED', statusCode: 401 }));
        }

        logger.info('[InvitationService] Accepting invitation', { invitationId, userId });

        if (!invitationId) {
            return err(new AppError('Invalid invitation ID', { code: 'INVALID_ID' }));
        }

        const { data, error } = await supabase.rpc('accept_board_invitation', {
            invitation_id: invitationId
        });

        if (error) {
            logger.error('[InvitationService] RPC failed', { error, invitationId });
            throw error;
        }

        // Check RPC result
        if (data && !data.success) {
            return err(new AppError(data.error || 'Failed to accept invitation', { code: 'INVITATION_ACCEPT_FAILED' }));
        }

        logger.info('[InvitationService] Invitation accepted via RPC', { invitationId, userId });
        return ok(undefined);

    } catch (error) {
        logger.error('[InvitationService] acceptInvitation failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Decline an invitation
 */
export async function declineInvitation(invitationId: string): Promise<ServiceResult<void>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            return err(new AppError('User not authenticated', { code: 'UNAUTHENTICATED', statusCode: 401 }));
        }

        const { error } = await supabase
            .from('board_invitations')
            .update({
                status: 'declined',
                invitee_id: userId,
                responded_at: new Date().toISOString(),
            })
            .eq('id', invitationId);

        if (error) throw error;

        logger.info('[InvitationService] Invitation declined', { invitationId, userId });
        return ok(undefined);

    } catch (error) {
        logger.error('[InvitationService] declineInvitation failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Cancel/revoke an invitation (for board owners)
 */
export async function cancelInvitation(invitationId: string): Promise<ServiceResult<void>> {
    try {
        const { error } = await supabase
            .from('board_invitations')
            .delete()
            .eq('id', invitationId);

        if (error) throw error;

        logger.info('[InvitationService] Invitation cancelled', { invitationId });
        return ok(undefined);

    } catch (error) {
        logger.error('[InvitationService] cancelInvitation failed', error);
        return err(AppError.from(error));
    }
}
